import { Monitor, Zap, Cable, Wrench, Check } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentTitle';

const SERVICES = [
  {
    icon: Monitor,
    title: 'LED Digital Screens',
    image: '/services/eastwide-led.jpg',
    description: 'We specialise in the professional installation and servicing of LED digital screens for commercial, retail, and industrial applications. From large-format outdoor displays to indoor signage solutions, our team handles the complete electrical infrastructure required to power and maintain your digital screens.',
    features: [
      'Full electrical installation and wiring',
      'Screen mounting and structural support',
      'Power supply configuration',
      'Ongoing maintenance and servicing',
      'Compliance with Australian electrical standards'
    ]
  },
  {
    icon: Zap,
    title: 'Mains Upgrades',
    image: '/services/mains-upgrades.jpg',
    description: 'As your electrical demands grow, your mains supply needs to keep up. We provide comprehensive mains upgrades for both residential and commercial properties, ensuring your power supply can handle modern appliances, equipment, and increased loads safely and efficiently.',
    features: [
      'Switchboard upgrades and replacements',
      'Single to three-phase conversions',
      'Meter board installations',
      'Safety switch installations',
      'Capacity assessments and planning'
    ]
  },
  {
    icon: Cable,
    title: 'Underground Wiring',
    image: '/underground-wiring.jpg',
    description: 'Underground electrical wiring provides a clean, safe, and weather-resistant solution for running power across your property. Whether connecting a new building, outdoor lighting, or relocating overhead lines, we manage the entire underground cabling process from trenching to final connection.',
    features: [
      'Underground cable installation',
      'Trenching and conduit laying',
      'Overhead to underground conversions',
      'Garden and landscape lighting wiring',
      'New property power connections'
    ]
  },
  {
    icon: Wrench,
    title: 'Commercial Maintenance',
    image: '/services/commercial-maintenance.jpg',
    description: 'Keep your business running smoothly with our comprehensive commercial electrical maintenance services. We provide scheduled and reactive maintenance to minimise downtime and ensure all your electrical systems remain safe, compliant, and operational.',
    features: [
      'Scheduled preventative maintenance',
      'Emergency breakdown response',
      'Lighting repairs and replacements',
      'Exit and emergency light testing',
      'Electrical safety inspections'
    ]
  }
];

export default function Services() {
  useDocumentMeta(
    "Electrical Services | LED Screens, Mains Upgrades, Underground Wiring - Eastwide Electrical",
    "Eastwide Electrical Services delivers LED digital screen installation, mains upgrades, underground wiring, and commercial maintenance across Victoria. Registered electrical contractor."
  );

  return (
    <main>
      {/* Page Header */}
      <section data-testid="services-header" className="bg-[#3D3D3D] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#7AC943] text-sm uppercase tracking-[0.2em] font-semibold mb-4 fade-up stagger-1">What We Do</p>
          <h1 data-testid="services-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight fade-up stagger-2">
            Our Services
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-xl mt-6 leading-relaxed fade-up stagger-3">
            As a registered electrical contractor, we deliver professional electrical services across Victoria.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24 md:space-y-32">
            {SERVICES.map((service, i) => (
              <div
                key={service.title}
                data-testid={`service-detail-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'lg:direction-rtl' : ''}`}
              >
                {/* Image */}
                <div className={`overflow-hidden rounded-lg ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-72 lg:h-96 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#7AC943]/10 rounded-sm flex items-center justify-center">
                      <service.icon size={20} className="text-[#7AC943]" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl text-[#3D3D3D] tracking-tight">
                      {service.title}
                    </h2>
                  </div>

                  <p className="text-gray-500 text-base leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <ul className="space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check size={16} className="text-[#7AC943] mt-0.5 flex-shrink-0" />
                        <span className="text-[#3D3D3D] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <a
                      href="tel:0409015781"
                      className="inline-flex items-center gap-2 bg-[#7AC943] text-black text-sm font-semibold uppercase tracking-[0.1em] px-6 py-3 rounded-sm hover:bg-[#68B038] transition-colors duration-300"
                    >
                      Enquire Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#7AC943] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-black tracking-tight mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-black/70 text-base mb-8 max-w-lg mx-auto">
            Contact us today for a free quote on any of our electrical services.
          </p>
          <a
            href="tel:0409015781"
            data-testid="services-cta-button"
            className="inline-flex items-center gap-2 bg-[#3D3D3D] text-white text-sm font-semibold uppercase tracking-[0.1em] px-8 py-3.5 rounded-sm hover:bg-black transition-colors duration-300"
          >
            Call 0409 015 781
          </a>
        </div>
      </section>
    </main>
  );
}
